/* eslint-disable camelcase */
import React, { useState, useRef } from 'react';
import { shape, string, func } from 'prop-types';
import { useQuery } from '@apollo/react-hooks';
import styled from 'styled-components/macro';
import {
  TextField,
  Paper,
  InputAdornment,
  Typography,
  IconButton,
} from '@material-ui/core';
import Search from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import { withRouter } from 'react-router-dom';

import SEARCH_PRODUCTS from './product-search-query';
import { allColors } from '../../constants/styles';
import MyTable from '../Table';

const SearchBarWrapper = styled.div`
  margin-left: auto;
  margin-right: 25px;
  & fieldset {
    border-width: 2px;
  }
`;

const StyledTextField = styled(TextField)`
  color: ${allColors.backgroundLightGrey};
  height: 38px;
  width: 309px;
  flex-direction: row;
  justify-content: flex-end;
  &:active {
    border-color: ${allColors.backgroundLightGrey};
  }
`;

const SearchIcon = styled(Search)`
  transform: rotate(90deg);
  color: ${allColors.backgroundDarkGrey};
`;

const searchColumns = [
  { width: 200, label: 'Item Number', dataKey: 'itemNumber' },
  { width: 150, label: 'Family', dataKey: 'family' },
  { width: 150, label: 'Model', dataKey: 'model' },
  { width: 130, label: 'Type', dataKey: 'type' },
  { width: 150, label: 'Cavity', dataKey: 'cavity' },
  { width: 150, label: 'Sub1', dataKey: 'sub1' },
  { width: 150, label: 'Sub2', dataKey: 'sub2' },
  { width: 130, label: 'Sub3', dataKey: 'sub3' },
  { width: 130, label: 'Sub4', dataKey: 'sub4' },
  { width: 130, label: 'Sub5', dataKey: 'sub5' },
  {
    width: 180,
    label: 'Additional Info1',
    dataKey: 'additionalNfo1',
    wrap: true,
  },
  {
    width: 180,
    label: 'Additional Info 2',
    dataKey: 'additionalNfo2',
    wrap: true,
  },
  { width: 300, label: 'Info 1', dataKey: 'info1', wrap: true },
  { width: 160, label: 'Info 2', dataKey: 'info2', wrap: true },
  { width: 160, label: 'Info 3', dataKey: 'info3', wrap: true },
  { width: 180, label: 'Desc 1', dataKey: 'desc1' },
  { width: 180, label: 'Desc 2', dataKey: 'desc2' },
  { width: 120, label: 'Picture', dataKey: 'picture' },
  { width: 180, label: 'Material', dataKey: 'material' },
  { width: 150, label: 'Weight', dataKey: 'weight' },
];

const DropdownPaper = styled(Paper)`
  position: absolute;
  top: 64px;
  left: 0;
  width: 100%;
  height: 500px;
  z-index: 100;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

export const Dropdown = ({
  data: { search_products },
  keyword,
  handleRowClick,
}) => {
  const scrollRef = useRef(null);

  if (search_products && search_products.length !== 0) {
    return (
      <DropdownPaper ref={scrollRef} data-testid="search-dropdown-test">
        <MyTable
          data={search_products}
          columns={searchColumns}
          scrollableDiv={scrollRef}
          onRowClick={p => handleRowClick(p.rowData.id)}
          hideToolbar
          storageKey="search"
        />
      </DropdownPaper>
    );
  }
  return (
    <DropdownPaper>
      <Typography style={{ padding: 16, color: '#666' }}>
        Your search - {keyword} - did not match any products.
      </Typography>
    </DropdownPaper>
  );
};

Dropdown.propTypes = {
  data: shape({}),
  keyword: string,
  handleRowClick: func,
};

Dropdown.defaultProps = {
  data: {},
  keyword: '',
  handleRowClick: () => {},
};

const SearchBar = ({ history }) => {
  const [keyword, setKeyword] = useState('');
  const [toggle, setToggle] = useState(true);
  const { data, error } = useQuery(SEARCH_PRODUCTS, {
    variables: { search: keyword },
    skip: keyword.length < 3,
    suspend: false,
  });

  const handleRowClick = rowId => {
    setToggle(false);
    setKeyword('');
    history.push(`/product/${rowId}`);
  };

  const handleOnChange = event => {
    setToggle(true);
    setKeyword(event.target.value);
  };

  if (error) {
    return <p>Error ${error.message}</p>;
  }
  return (
    <SearchBarWrapper>
      <StyledTextField
        placeholder="Search your item here"
        onChange={event => handleOnChange(event)}
        value={keyword}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: keyword ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  setKeyword('');
                  setToggle(false);
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        variant="outlined"
        data-testid="searchbar-test"
      />
      {data && toggle && (
        <Dropdown
          data={data}
          keyword={keyword}
          history={history}
          handleRowClick={handleRowClick}
        />
      )}
    </SearchBarWrapper>
  );
};

SearchBar.propTypes = {
  history: shape({}).isRequired,
};

export default withRouter(SearchBar);
